import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Rocket, BookOpen, Globe, Brain, BarChart3, Menu, X, Target, Crown, MessageSquare, LogIn, LogOut, User } from 'lucide-react';
import { useState } from 'react';
import { useProgress } from '../hooks/useProgress';
import { useAuth } from '../hooks/useAuth';
import { PremiumModal } from './PremiumModal';

const navLinks = [
  { to: '/learn', label: 'Learn', icon: BookOpen },
  { to: '/explore', label: 'Explore', icon: Globe },
  { to: '/sandbox', label: 'Sandbox', icon: Rocket },
  { to: '/challenge', label: 'Challenge', icon: Target },
  { to: '/quiz', label: 'Quiz', icon: Brain },
  { to: '/dashboard', label: 'Mission Log', icon: BarChart3 },
];

export function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { progress } = useProgress();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/');
  };

  return (
    <>
      <nav
        data-testid="navigation"
        className="fixed top-0 left-0 right-0 z-50 h-16"
        style={{ background: 'rgba(3,3,5,0.85)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,240,255,0.1)' }}
      >
        <div className="flex items-center justify-between h-full px-6 max-w-screen-2xl mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(0,240,255,0.1)', border: '1px solid rgba(0,240,255,0.3)' }}>
              <Rocket size={16} color="#00F0FF" />
            </div>
            <span className="font-bold text-lg tracking-widest uppercase text-white group-hover:text-cyan-400" style={{ fontFamily: 'Rajdhani', transition: 'color 0.2s' }}>
              OrbitOps
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(({ to, label, icon: Icon }) => {
              const isActive = location.pathname.startsWith(to);
              return (
                <Link
                  key={to} to={to}
                  data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium tracking-wide"
                  style={{
                    fontFamily: 'Rajdhani', color: isActive ? '#00F0FF' : 'rgba(255,255,255,0.6)',
                    background: isActive ? 'rgba(0,240,255,0.08)' : 'transparent',
                    border: isActive ? '1px solid rgba(0,240,255,0.2)' : '1px solid transparent',
                    transition: 'all 0.2s', letterSpacing: '0.1em', textTransform: 'uppercase',
                  }}
                >
                  <Icon size={14} />{label}
                </Link>
              );
            })}
            {/* Contact */}
            <Link
              to="/contact"
              data-testid="nav-contact"
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
              style={{
                fontFamily: 'Rajdhani',
                color: location.pathname === '/contact' ? '#00F0FF' : 'rgba(255,255,255,0.6)',
                background: location.pathname === '/contact' ? 'rgba(0,240,255,0.08)' : 'transparent',
                border: location.pathname === '/contact' ? '1px solid rgba(0,240,255,0.2)' : '1px solid transparent',
                letterSpacing: '0.1em', textTransform: 'uppercase', transition: 'all 0.2s',
              }}
            >
              <MessageSquare size={14} />Contact
            </Link>
          </div>

          {/* Right side: Premium + XP + User */}
          <div className="flex items-center gap-3">
            {progress.isPremium ? (
              <div data-testid="premium-status" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                style={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,150,0,0.1))', border: '1px solid rgba(255,215,0,0.4)' }}>
                <Crown size={14} color="#FFD700" />
                <span style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', color: '#FFD700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Premium</span>
              </div>
            ) : (
              <button onClick={() => setShowPremiumModal(true)} data-testid="upgrade-nav-btn"
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                style={{ background: 'rgba(255,215,0,0.08)', border: '1px solid rgba(255,215,0,0.25)', cursor: 'pointer' }}>
                <Crown size={14} color="#FFD700" />
                <span style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', color: '#FFD700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Upgrade</span>
              </button>
            )}

            <div data-testid="points-display" className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg"
              style={{ background: 'rgba(0,240,255,0.08)', border: '1px solid rgba(0,240,255,0.25)' }}>
              <span style={{ fontSize: '12px', fontFamily: 'JetBrains Mono', color: '#00F0FF' }}>XP</span>
              <span style={{ fontFamily: 'JetBrains Mono', color: '#00F0FF', fontSize: '14px', fontWeight: 600 }}>{progress.totalPoints.toLocaleString()}</span>
            </div>

            {/* User Menu */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(v => !v)}
                  data-testid="user-menu-btn"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer' }}
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />
                  ) : (
                    <User size={16} color="rgba(255,255,255,0.6)" />
                  )}
                  <span className="hidden sm:inline" style={{ fontFamily: 'Rajdhani', fontSize: '13px', color: 'rgba(255,255,255,0.7)', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {user.name?.split(' ')[0]}
                  </span>
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 top-12 w-48 rounded-xl py-2"
                    style={{ background: 'rgba(10,12,18,0.98)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                    <div className="px-4 py-2 mb-1" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <p style={{ fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '14px', color: '#fff' }}>{user.name}</p>
                      <p style={{ fontFamily: 'JetBrains Mono', fontSize: '10px', color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user.email}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      data-testid="logout-btn"
                      className="w-full flex items-center gap-2 px-4 py-2 text-left"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'Rajdhani', fontSize: '13px', color: '#FF4444', transition: 'background 0.15s' }}
                    >
                      <LogOut size={14} />Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="login-nav-btn"
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
                style={{ background: 'rgba(0,240,255,0.08)', border: '1px solid rgba(0,240,255,0.25)', fontFamily: 'Rajdhani', fontWeight: 600, fontSize: '12px', color: '#00F0FF', textTransform: 'uppercase', letterSpacing: '0.06em', textDecoration: 'none' }}
              >
                <LogIn size={14} />Sign In
              </Link>
            )}

            <button onClick={() => setMenuOpen(v => !v)} className="md:hidden p-2 rounded-lg"
              style={{ color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.05)' }} data-testid="mobile-menu-toggle">
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden absolute left-0 right-0 top-16 py-3 px-4 flex flex-col gap-1"
            style={{ background: 'rgba(3,3,5,0.97)', borderBottom: '1px solid rgba(0,240,255,0.15)' }}>
            {navLinks.map(({ to, label, icon: Icon }) => (
              <Link key={to} to={to} onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm"
                style={{ fontFamily: 'Rajdhani', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                <Icon size={15} />{label}
              </Link>
            ))}
            <Link to="/contact" onClick={() => setMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm"
              style={{ fontFamily: 'Rajdhani', color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
              <MessageSquare size={15} />Contact
            </Link>
            <div className="flex items-center justify-between px-4 py-3 mt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <span style={{ fontSize: '12px', fontFamily: 'JetBrains Mono', color: '#00F0FF' }}>XP: {progress.totalPoints}</span>
            </div>
          </div>
        )}
      </nav>

      <PremiumModal isOpen={showPremiumModal} onClose={() => setShowPremiumModal(false)} />
    </>
  );
}
